import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const fmt = n => (n || 0).toLocaleString('tr-TR');

// Temel birim mg (veya g). ml kullanilmaz.
const birimYaz = (miktar, birim) => fmt(miktar) + ' ' + (birim || 'mg');
const kgCevir = (miktar, birim) => {
  const m = Number(miktar) || 0;
  if (birim === 'g') return m / 1000;
  return m / 1000000;              // mg -> kg
};
const kgYaz = (miktar, birim) => kgCevir(miktar, birim).toLocaleString('tr-TR', { maximumFractionDigits: 3 }) + ' kg';

export default function EtkenMaddeler() {
  const [etkenMaddeler, setEtkenMaddeler] = useState([]);
  const [urunler, setUrunler] = useState([]);
  const [firmalar, setFirmalar] = useState([]);
  const [tumUEM, setTumUEM] = useState([]);            // tum urun-etken madde baglantilari
  const [urunMiktarMap, setUrunMiktarMap] = useState({}); // urun_id -> satilan adet
  const [secilenEM, setSecilenEM] = useState(null);
  const [tarihBas, setTarihBas] = useState('');
  const [tarihBit, setTarihBit] = useState('');
  const [numuneDahil, setNumuneDahil] = useState(true);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [urunModal, setUrunModal] = useState(false);
  const [form, setForm] = useState({ ad: '', birim: 'mg', uyari_1: 10000000, uyari_2: 14000000 });
  const [urunFirmaFiltre, setUrunFirmaFiltre] = useState('');
  const [urunArama, setUrunArama] = useState('');
  const [secilenUrunler, setSecilenUrunler] = useState({}); // urun_id -> miktar

  // ---------- Veri cekme ----------

  // Supabase 1000 satir limitini asmak icin sayfali cekim
  async function tumKalemleriGetir(urunIds, bas, bit, numuneVar) {
    const hepsi = [];
    const SAYFA = 1000;
    for (let i = 0; i < 100; i++) {
      let q = supabase.from('siparis_kalemleri')
        .select('miktar, urun_id, siparisler!inner(created_at, siparis_durumu, siparis_tipi)')
        .in('urun_id', urunIds)
        .neq('siparisler.siparis_durumu', 'iptal');

      if (!numuneVar) q = q.neq('siparisler.siparis_tipi', 'numune');
      if (bas) q = q.gte('siparisler.created_at', bas);
      if (bit) q = q.lte('siparisler.created_at', bit + 'T23:59:59');

      const { data, error } = await q.range(i * SAYFA, (i + 1) * SAYFA - 1);
      if (error) { console.error('kalem cekme hatasi', error); break; }
      hepsi.push(...(data || []));
      if (!data || data.length < SAYFA) break;
    }
    return hepsi;
  }

  const hesapla = useCallback(async (bas, bit, numuneVar) => {
    setLoading(true);

    const [{ data: em }, { data: u }, { data: f }, { data: uem }] = await Promise.all([
      supabase.from('etken_maddeler').select('*').eq('aktif', true).order('ad'),
      supabase.from('urunler').select('id, ad, alt_grup, firma_id').eq('aktif', true).order('ad'),
      supabase.from('firmalar').select('id, ad').eq('aktif', true),
      supabase.from('urun_etken_maddeler').select('id, urun_id, etken_madde_id, miktar_ml'),
    ]);

    setEtkenMaddeler(em || []);
    setUrunler(u || []);
    setFirmalar(f || []);
    setTumUEM(uem || []);

    const urunIds = [...new Set((uem || []).map(x => x.urun_id))];
    if (urunIds.length === 0) { setUrunMiktarMap({}); setLoading(false); return; }

    const kalemler = await tumKalemleriGetir(urunIds, bas, bit, numuneVar);

    const map = {};
    kalemler.forEach(k => {
      if (!k.siparisler) return;
      map[k.urun_id] = (map[k.urun_id] || 0) + (k.miktar || 0);
    });
    setUrunMiktarMap(map);
    setLoading(false);
  }, []);

  useEffect(() => { hesapla('', '', true); }, [hesapla]);

  function filtreUygula() { hesapla(tarihBas, tarihBit, numuneDahil); }
  function filtreTemizle() { setTarihBas(''); setTarihBit(''); setNumuneDahil(true); hesapla('', '', true); }

  // ---------- Hesaplamalar ----------

  const urunAd = id => urunler.find(u => u.id === id)?.ad || '-';

  // Her etken madde icin toplam kullanim
  const emToplam = {};
  tumUEM.forEach(uem => {
    const satilan = urunMiktarMap[uem.urun_id] || 0;
    emToplam[uem.etken_madde_id] = (emToplam[uem.etken_madde_id] || 0) + satilan * (Number(uem.miktar_ml) || 0);
  });

  const emDurum = em => {
    const t = emToplam[em.id] || 0;
    if (em.uyari_2 && t >= Number(em.uyari_2)) return { renk: 'var(--red)', etiket: '🚨 2. eşik aşıldı' };
    if (em.uyari_1 && t >= Number(em.uyari_1)) return { renk: 'var(--yellow)', etiket: '⚠️ 1. eşik aşıldı' };
    return { renk: 'var(--green)', etiket: '✅ Normal' };
  };

  // Secilen etken maddenin urun bazli dagilimi
  const secilenUEM = secilenEM ? tumUEM.filter(x => x.etken_madde_id === secilenEM.id) : [];
  const kullanimlar = secilenUEM.map(uem => {
    const satilanUrun = urunMiktarMap[uem.urun_id] || 0;
    return {
      id: uem.id,
      urun_id: uem.urun_id,
      urun_ad: urunAd(uem.urun_id),
      birim_miktar: Number(uem.miktar_ml) || 0,
      satilanUrun,
      kullanilan: satilanUrun * (Number(uem.miktar_ml) || 0),
    };
  }).sort((a, b) => b.kullanilan - a.kullanilan);

  const kullanilanlar = kullanimlar.filter(k => k.satilanUrun > 0);
  const secilenToplam = secilenEM ? (emToplam[secilenEM.id] || 0) : 0;

  // ---------- Kayit islemleri ----------

  async function emKaydet() {
    if (!form.ad.trim()) return;
    const { error } = await supabase.from('etken_maddeler').insert({
      ad: form.ad.trim(), birim: form.birim,
      uyari_1: Number(form.uyari_1) || null, uyari_2: Number(form.uyari_2) || null,
      aktif: true,
    });
    if (error) { alert('Kayıt hatası: ' + error.message); return; }
    setModal(false);
    setForm({ ad: '', birim: 'mg', uyari_1: 10000000, uyari_2: 14000000 });
    hesapla(tarihBas, tarihBit, numuneDahil);
  }

  async function urunEMKaydet() {
    const kayitlar = Object.entries(secilenUrunler)
      .filter(([, m]) => m !== '' && m != null && Number(m) > 0);
    if (kayitlar.length === 0) return;

    for (const [urun_id, miktar] of kayitlar) {
      const { data: mevcut } = await supabase.from('urun_etken_maddeler')
        .select('id').eq('urun_id', urun_id).eq('etken_madde_id', secilenEM.id).maybeSingle();
      if (mevcut) {
        await supabase.from('urun_etken_maddeler').update({ miktar_ml: Number(miktar) }).eq('id', mevcut.id);
      } else {
        await supabase.from('urun_etken_maddeler')
          .insert({ urun_id, etken_madde_id: secilenEM.id, miktar_ml: Number(miktar) });
      }
    }
    setUrunModal(false);
    setSecilenUrunler({});
    setUrunArama('');
    hesapla(tarihBas, tarihBit, numuneDahil);
  }

  async function urunEMSil(id) {
    if (!window.confirm('Bu ürün-etken madde bağlantısını silmek istediğinizden emin misiniz?')) return;
    await supabase.from('urun_etken_maddeler').delete().eq('id', id);
    hesapla(tarihBas, tarihBit, numuneDahil);
  }

  function urunModalAc(em) {
    const hedef = em || secilenEM;
    if (!hedef) return;
    setSecilenEM(hedef);
    // Mevcut baglantilari on-doldur
    const mevcut = {};
    tumUEM.filter(x => x.etken_madde_id === hedef.id)
      .forEach(uem => { mevcut[uem.urun_id] = String(uem.miktar_ml || ''); });
    setSecilenUrunler(mevcut);
    setUrunArama('');
    setUrunFirmaFiltre('');
    setUrunModal(true);
  }

  const trLower = s => (s || '').toLocaleLowerCase('tr-TR');
  const modalUrunleri = urunler.filter(u =>
    (!urunFirmaFiltre || u.firma_id === urunFirmaFiltre) &&
    (!urunArama || trLower(u.ad).includes(trLower(urunArama)))
  );
  const secimSayisi = Object.values(secilenUrunler).filter(m => m !== '' && Number(m) > 0).length;

  // ---------- Render ----------

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">🧪 Etken Madde Takibi</h1>
          <p className="page-subtitle">{etkenMaddeler.length} etken madde</p>
        </div>
        <button className="btn btn-primary" onClick={() => setModal(true)}>+ Yeni Etken Madde</button>
      </div>

      {/* Global filtre */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={{ fontSize: 12, color: 'var(--text2)', display: 'block', marginBottom: 4 }}>Başlangıç</label>
            <input type="date" className="input" style={{ width: 150 }} value={tarihBas} onChange={e => setTarihBas(e.target.value)} />
          </div>
          <div>
            <label style={{ fontSize: 12, color: 'var(--text2)', display: 'block', marginBottom: 4 }}>Bitiş</label>
            <input type="date" className="input" style={{ width: 150 }} value={tarihBit} onChange={e => setTarihBit(e.target.value)} />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', paddingBottom: 8 }}>
            <input type="checkbox" checked={numuneDahil} onChange={e => setNumuneDahil(e.target.checked)} />
            <span style={{ fontSize: 13 }}>Numuneler dahil</span>
          </label>
          <button className="btn btn-primary btn-sm" onClick={filtreUygula}>🔍 Filtrele</button>
          {(tarihBas || tarihBit || !numuneDahil) && (
            <button className="btn btn-ghost btn-sm" onClick={filtreTemizle}>✕ Temizle</button>
          )}
          <div style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text2)' }}>
            {tarihBas || tarihBit
              ? `${tarihBas || '…'} → ${tarihBit || '…'}`
              : 'Tüm zamanlar'}
          </div>
        </div>
      </div>

      {/* ÖZET — tüm etken maddelerin toplam kullanımı */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ fontWeight: 700, fontSize: 15, marginBottom: 12 }}>📊 Toplam Kullanım Özeti</h3>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 20, color: 'var(--text2)' }}>Hesaplanıyor...</div>
        ) : etkenMaddeler.length === 0 ? (
          <div style={{ color: 'var(--text2)', fontSize: 13 }}>Henüz etken madde yok</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Etken Madde</th>
                <th>Ürün Sayısı</th>
                <th>Toplam Kullanım</th>
                <th>KG</th>
                <th>1. Eşik</th>
                <th>2. Eşik</th>
                <th>Kalan (2. eşiğe)</th>
                <th>Durum</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {etkenMaddeler.map(em => {
                const t = emToplam[em.id] || 0;
                const d = emDurum(em);
                const urunSayisi = tumUEM.filter(x => x.etken_madde_id === em.id).length;
                return (
                  <tr key={em.id} onClick={() => setSecilenEM(em)} style={{ cursor: 'pointer', background: secilenEM?.id === em.id ? 'rgba(224,123,0,0.08)' : 'transparent' }}>
                    <td style={{ fontWeight: 600 }}>{em.ad}</td>
                    <td style={{ color: 'var(--text2)' }}>{urunSayisi}</td>
                    <td style={{ fontWeight: 700, color: 'var(--accent)' }}>{birimYaz(t, em.birim)}</td>
                    <td style={{ fontWeight: 800, color: d.renk, fontSize: 15 }}>{kgYaz(t, em.birim)}</td>
                    <td style={{ fontSize: 12, color: 'var(--text2)' }}>{em.uyari_1 ? kgYaz(em.uyari_1, em.birim) : '-'}</td>
                    <td style={{ fontSize: 12, color: 'var(--text2)' }}>{em.uyari_2 ? kgYaz(em.uyari_2, em.birim) : '-'}</td>
                    <td style={{ fontSize: 12, fontWeight: 600, color: d.renk }}>
                      {em.uyari_2 ? kgYaz(Math.max(0, Number(em.uyari_2) - t), em.birim) : '-'}
                    </td>
                    <td style={{ fontSize: 12, color: d.renk, fontWeight: 600 }}>{d.etiket}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn btn-primary btn-sm"
                        onClick={e => { e.stopPropagation(); urunModalAc(em); }}>🧪 Ürünleri Yönet</button>
                      <button className="btn btn-ghost btn-sm" style={{ marginLeft: 6 }}
                        onClick={e => { e.stopPropagation(); setSecilenEM(em); }}>📋 Detay</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 8 }}>
          <b>🧪 Ürünleri Yönet</b> → hangi üründe kaç mg olduğunu girersin. <b>📋 Detay</b> → hangi üründen ne kadar tüketildiğini ürün ürün gösterir. İptal edilen siparişler hesaba katılmaz.
        </div>
      </div>

      {/* DETAY */}
      {secilenEM && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
            <h3 style={{ fontWeight: 700, fontSize: 16 }}>
              🧪 {secilenEM.ad}
              <span style={{ fontSize: 12, color: 'var(--text2)', fontWeight: 400, marginLeft: 8 }}>birim: {secilenEM.birim}</span>
            </h3>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary btn-sm" onClick={() => urunModalAc(secilenEM)}>🧪 Ürünleri Yönet</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setSecilenEM(null)}>✕ Kapat</button>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 16 }}>
            <div className="stat-card">
              <div className="stat-label">Toplam Kullanım</div>
              <div className="stat-value" style={{ fontSize: 18 }}>{birimYaz(secilenToplam, secilenEM.birim)}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">KG Cinsinden</div>
              <div className="stat-value" style={{ fontSize: 18, color: emDurum(secilenEM).renk }}>{kgYaz(secilenToplam, secilenEM.birim)}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">2. Eşiğe Kalan</div>
              <div className="stat-value" style={{ fontSize: 18 }}>
                {secilenEM.uyari_2 ? kgYaz(Math.max(0, Number(secilenEM.uyari_2) - secilenToplam), secilenEM.birim) : '-'}
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Bağlı Ürün</div>
              <div className="stat-value" style={{ fontSize: 18 }}>{secilenUEM.length}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Durum</div>
              <div className="stat-value" style={{ fontSize: 14, color: emDurum(secilenEM).renk }}>{emDurum(secilenEM).etiket}</div>
            </div>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 20, color: 'var(--text2)' }}>Yükleniyor...</div>
          ) : kullanilanlar.length > 0 ? (
            <table className="table">
              <thead>
                <tr><th>Ürün</th><th>Ürün Başına</th><th>Satılan</th><th>Toplam Kullanım</th><th>Pay</th></tr>
              </thead>
              <tbody>
                {kullanilanlar.map(k => (
                  <tr key={k.id}>
                    <td style={{ fontWeight: 500 }}>{k.urun_ad}</td>
                    <td style={{ color: 'var(--text2)' }}>{birimYaz(k.birim_miktar, secilenEM.birim)}</td>
                    <td>{fmt(k.satilanUrun)} adet</td>
                    <td style={{ fontWeight: 700, color: 'var(--accent)' }}>{birimYaz(k.kullanilan, secilenEM.birim)}</td>
                    <td style={{ color: 'var(--text2)', fontSize: 12 }}>
                      {secilenToplam > 0 ? ((k.kullanilan / secilenToplam) * 100).toFixed(1) + '%' : '-'}
                    </td>
                  </tr>
                ))}
                <tr style={{ borderTop: '2px solid var(--border)', background: 'var(--bg3)' }}>
                  <td colSpan={3} style={{ fontWeight: 700 }}>TOPLAM</td>
                  <td style={{ fontWeight: 800, color: 'var(--accent)', fontSize: 15 }}>
                    {birimYaz(secilenToplam, secilenEM.birim)}
                  </td>
                  <td style={{ fontWeight: 800, fontSize: 13, color: emDurum(secilenEM).renk }}>{kgYaz(secilenToplam, secilenEM.birim)}</td>
                </tr>
              </tbody>
            </table>
          ) : (
            <div style={{ textAlign: 'center', padding: 20, color: 'var(--text2)' }}>
              {secilenUEM.length === 0 ? 'Henüz ürün eklenmemiş' : 'Bu dönemde satış yok'}
            </div>
          )}

          {/* Bagli urunler */}
          <div style={{ marginTop: 20 }}>
            <h4 style={{ fontWeight: 700, marginBottom: 12, fontSize: 14 }}>Bu etken maddeyi içeren ürünler ({secilenUEM.length})</h4>
            {secilenUEM.length === 0 ? (
              <div style={{ color: 'var(--text2)', fontSize: 13 }}>Henüz ürün eklenmemiş</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {kullanimlar.map(k => (
                  <div key={k.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: 'var(--bg3)', borderRadius: 'var(--r)', border: '1px solid var(--border)' }}>
                    <div>
                      <span style={{ fontWeight: 500 }}>{k.urun_ad}</span>
                      <span style={{ marginLeft: 12, color: 'var(--accent)', fontWeight: 700 }}>
                        {birimYaz(k.birim_miktar, secilenEM.birim)} / ürün
                      </span>
                    </div>
                    <button className="btn btn-danger btn-sm" onClick={() => urunEMSil(k.id)}>Sil</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Yeni Etken Madde Modal */}
      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">Yeni Etken Madde</h3>
            <div className="form-group">
              <label className="label">Etken Madde Adı *</label>
              <input className="input" value={form.ad} onChange={e => setForm(f => ({ ...f, ad: e.target.value }))} placeholder="örn: Molnupiravir" autoFocus />
            </div>
            <div className="form-group">
              <label className="label">Birim</label>
              <select className="select" value={form.birim} onChange={e => setForm(f => ({ ...f, birim: e.target.value }))}>
                <option value="mg">mg</option>
                <option value="g">g</option>
              </select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div className="form-group">
                <label className="label">⚠️ 1. Uyarı Eşiği ({form.birim})</label>
                <input className="input" type="number" value={form.uyari_1} onChange={e => setForm(f => ({ ...f, uyari_1: e.target.value }))} />
                <span style={{ fontSize: 11, color: 'var(--text2)' }}>= {kgYaz(form.uyari_1, form.birim)}</span>
              </div>
              <div className="form-group">
                <label className="label">🚨 2. Uyarı Eşiği ({form.birim})</label>
                <input className="input" type="number" value={form.uyari_2} onChange={e => setForm(f => ({ ...f, uyari_2: e.target.value }))} />
                <span style={{ fontSize: 11, color: 'var(--text2)' }}>= {kgYaz(form.uyari_2, form.birim)}</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn-ghost" onClick={() => setModal(false)}>İptal</button>
              <button className="btn btn-primary" onClick={emKaydet} disabled={!form.ad.trim()}>Kaydet</button>
            </div>
          </div>
        </div>
      )}

      {/* Ürün Ekle Modal — ÇOKLU */}
      {urunModal && secilenEM && (
        <div className="modal-overlay" onClick={() => setUrunModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
            <h3 className="modal-title">Ürün Ekle / Düzenle — {secilenEM.ad}</h3>
            <p style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 12 }}>
              Birden fazla ürün seçebilirsin. Her ürün için ürün başına düşen {secilenEM.birim} miktarını gir. Boş bırakılan ürünler kaydedilmez.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
              <select className="select" value={urunFirmaFiltre} onChange={e => setUrunFirmaFiltre(e.target.value)}>
                <option value="">Tüm Firmalar</option>
                {firmalar.map(f => <option key={f.id} value={f.id}>{f.ad}</option>)}
              </select>
              <input className="input" placeholder="Ürün ara..." value={urunArama} onChange={e => setUrunArama(e.target.value)} />
            </div>

            <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--r)' }}>
              {modalUrunleri.length === 0 && (
                <div style={{ padding: 16, color: 'var(--text2)', fontSize: 13, textAlign: 'center' }}>Ürün bulunamadı</div>
              )}
              {modalUrunleri.map(u => {
                const deger = secilenUrunler[u.id] ?? '';
                const secili = deger !== '' && Number(deger) > 0;
                return (
                  <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderBottom: '1px solid var(--border)', background: secili ? 'rgba(224,123,0,0.06)' : 'transparent' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.ad}</div>
                      <div style={{ fontSize: 11, color: 'var(--text2)' }}>{u.alt_grup || 'Diğer'}</div>
                    </div>
                    <input className="input" type="number" style={{ width: 110 }} placeholder={secilenEM.birim}
                      value={deger}
                      onChange={e => setSecilenUrunler(s => ({ ...s, [u.id]: e.target.value }))} />
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
              <span style={{ fontSize: 12, color: 'var(--text2)' }}>{secimSayisi} ürün seçili</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-ghost" onClick={() => setUrunModal(false)}>İptal</button>
                <button className="btn btn-primary" onClick={urunEMKaydet} disabled={secimSayisi === 0}>Kaydet ({secimSayisi})</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
